import React, { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useState } from 'react';
import { StepItem, StepsIndexConstant } from './Stepper';

type ExistingStepData = {
    step: number;
    values: { [key: string]: any };
};

export type NextActionOverride = {
    labelToken: string;
    onClick: () => void;
    disabled?: boolean;
};
interface StepperContextProps {
    activeStep: number;
    setActiveStep: Dispatch<SetStateAction<number>>;
    goToNextStep: () => void;
    goToPreviousStep: () => void;
    setStepCompletion: (step: number, isValid: boolean) => void;
    checkIsStepComplete: (step: number) => boolean;
    setStepData: (step: number, data: any) => void;
    getStepData: (step: number) => any;
    setIsSaveLoading: (isLoading: boolean) => void;
    getIsSaveLoading: () => boolean;
    setTemplateData: (step: number, data: any) => void;
    getTemplateData: (step: number) => Array<any>;
    setExistingStepData: (existingData: Array<ExistingStepData>) => void;
    isEditMode?: boolean;
    setIsEditMode?: Dispatch<SetStateAction<boolean>>;
    getAllStepData?: (steps: StepItem[]) => any;
    disableDefaultNextAction?: boolean;
    setDisableDefaultNextAction?: Dispatch<SetStateAction<boolean>>;
    nextActionOverride: NextActionOverride | null;
    setNextActionOverride: Dispatch<SetStateAction<NextActionOverride | null>>;
    // Lets the active step suppress the Skip button of an optional step; steps that set it must reset it on unmount.
    hideSkipButton: boolean;
    setHideSkipButton: Dispatch<SetStateAction<boolean>>;
}

const StepperContext = createContext<StepperContextProps | null>(null);

type StepperProviderProps = {
    children: ReactNode;
    /** The step the stepper opens on, for one that continues earlier work. */
    initialActiveStep?: number;
    /** The data the steps hold when the stepper opens, by step index, for one that continues earlier work. */
    initialStepData?: Record<number, any>;
};

export const StepperProvider = ({
    children,
    initialActiveStep = StepsIndexConstant.zero,
    initialStepData = {}
}: StepperProviderProps) => {
    const [activeStep, setActiveStep] = useState(initialActiveStep);
    const [isStepComplete, setIsStepComplete] = useState<Record<number, boolean>>({});
    const [stepData, setStepDataState] = useState<Record<number, any>>(initialStepData);
    const [templateData, setStepTemplateData] = useState<any>({});
    const [isStepperSaveLoading, setIsStepperSaveLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [disableDefaultNextAction, setDisableDefaultNextAction] = useState(false);
    const [nextActionOverride, setNextActionOverride] = useState<NextActionOverride | null>(null);
    const [hideSkipButton, setHideSkipButton] = useState(false);

    const setStepData = useCallback((step: number, data: any) => {
        setStepDataState((prev) => ({ ...prev, [step]: data }));
    }, []);

    const getStepData = useCallback(
        (step: number) => {
            return stepData[step] || null;
        },
        [stepData]
    );

    const getTemplateData = useCallback(
        (step: number) => {
            return templateData[step] || null;
        },
        [templateData]
    );

    const setTemplateData = useCallback((step: number, data: any) => {
        setStepTemplateData((prev) => ({ ...prev, [step]: data }));
    }, []);

    const goToNextStep = useCallback(() => setActiveStep((prev) => prev + 1), []);
    const goToPreviousStep = useCallback(() => setActiveStep((prev) => prev - 1), []);

    const setStepCompletion = useCallback((step: number, isValid: boolean) => {
        setIsStepComplete((prev) => ({ ...prev, [step]: isValid }));
    }, []);

    const checkIsStepComplete = useCallback((step: number) => isStepComplete[step] ?? true, [isStepComplete]);

    const setIsSaveLoading = useCallback((isLoading: boolean) => setIsStepperSaveLoading(isLoading), []);

    const getIsSaveLoading = useCallback(() => isStepperSaveLoading, [isStepperSaveLoading]);

    const setExistingStepData = useCallback(
        (existingData: Array<ExistingStepData>) => {
            existingData.forEach((data: ExistingStepData) => setStepData(data.step, data.values));
        },
        [setStepData]
    );

    const getAllStepData = useCallback(
        (steps: StepItem[]) => {
            return steps.reduce((acc, step, index) => {
                const data = getStepData(index); // Retrieve data for each step
                return { ...acc, [`${index}`]: data };
            }, {}) as any;
        },
        [getStepData]
    );

    return (
        <StepperContext.Provider
            value={{
                activeStep,
                setActiveStep,
                isEditMode,
                setIsEditMode,
                disableDefaultNextAction,
                setDisableDefaultNextAction,
                nextActionOverride,
                setNextActionOverride,
                hideSkipButton,
                setHideSkipButton,
                setStepData,
                getStepData,
                getTemplateData,
                setTemplateData,
                goToNextStep,
                goToPreviousStep,
                setStepCompletion,
                checkIsStepComplete,
                setIsSaveLoading,
                getIsSaveLoading,
                setExistingStepData,
                getAllStepData
            }}
        >
            {children}
        </StepperContext.Provider>
    );
};

export const useStepper = (strict: boolean = true) => {
    const context = useContext(StepperContext);
    if (strict && !context) throw new Error('useStepper must be used within a StepperProvider');
    return context ?? null;
};
